import { useRef, useSyncExternalStore } from "react";
import { decodeJwt } from "jose";

//#region src/client/hooks/use-auth-tokens.ts
const CSRF_STATE_KEY = "secureState";
const TOKEN_LOCALSTORAGE_KEY = "tokens";
/**
* Operation guard flag to prevent token updates during intentional logout.
* This flag prevents a race condition where in-flight token refresh
* operations complete after logout has been initiated, potentially
* restoring tokens that should remain cleared.
*
* Only set when clearStoredTokens is called with intentionalLogout=true.
* A failed token refresh clears tokens without setting this flag so that
* a concurrent login callback (e.g. registration) can still store new tokens.
*
* @see MYPT-356 for the original race condition
*/
let isLoggingOut = false;
const clearStoredTokens = (intentionalLogout = false) => {
	if (intentionalLogout) isLoggingOut = true;
	window.localStorage.removeItem(CSRF_STATE_KEY);
	window.localStorage.removeItem(TOKEN_LOCALSTORAGE_KEY);
	window.dispatchEvent(new StorageEvent("storage", {
		key: TOKEN_LOCALSTORAGE_KEY,
		newValue: null
	}));
};
const updateStoredTokens = (payload) => {
	if (isLoggingOut) {
		console.warn("[Auth] Token update blocked during logout - race condition prevented", {
			operation: "updateStoredTokens",
			state: "loggingOut",
			timestamp: Date.now()
		});
		return;
	}
	try {
		const newToken = decodeJwt(payload.accessToken);
		const clockSkew = newToken.iat ? Date.now() - newToken.iat * 1e3 : 0;
		const newValue = {
			accessToken: {
				...newToken,
				raw: payload.accessToken
			},
			refreshToken: payload.refreshToken,
			expMs: newToken.exp * 1e3 + clockSkew
		};
		window.localStorage.removeItem(CSRF_STATE_KEY);
		window.localStorage.setItem(TOKEN_LOCALSTORAGE_KEY, JSON.stringify(newValue));
		window.dispatchEvent(new StorageEvent("storage", {
			key: TOKEN_LOCALSTORAGE_KEY,
			newValue: JSON.stringify(newValue)
		}));
		isLoggingOut = false;
	} catch (error) {
		console.error(error, JSON.stringify(payload));
	}
};
const useAuthTokens = () => {
	const latestKey = useRef(null);
	const latestValue = useRef(null);
	const localStore = {
		getSnapshot: () => {
			const storedData = localStorage.getItem(TOKEN_LOCALSTORAGE_KEY);
			if (!storedData) {
				latestKey.current = null;
				latestValue.current = null;
				return null;
			}
			try {
				if (storedData === latestKey.current && latestValue.current !== null) return latestValue.current;
				latestKey.current = storedData;
				latestValue.current = JSON.parse(storedData);
				return latestValue.current;
			} catch (error) {
				console.error(error);
				return null;
			}
		},
		subscribe: (listener) => {
			window.addEventListener("storage", listener);
			return () => void window.removeEventListener("storage", listener);
		}
	};
	return useSyncExternalStore(localStore.subscribe, localStore.getSnapshot);
};

//#endregion
export { CSRF_STATE_KEY, TOKEN_LOCALSTORAGE_KEY, clearStoredTokens, updateStoredTokens, useAuthTokens };